import { memo, useId } from 'react';
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useTheme } from '../../theme';
import { formatCompact, formatDate, formatDateShort } from '../../utils';
import { ChartTooltip } from '../ui/ChartTooltip';

export const TrendChart = memo(
  ({ data, valueFormatter, currentLabel, previousLabel, showPrevious = true, height = 300, color }) => {
    const { palette } = useTheme();
    const id = useId().replace(/:/g, '');
    const stroke = color ?? palette.chart[0];

    return (
      <div className='wm-chart' style={{ height }}>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`trend-${id}`} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={stroke} stopOpacity={palette.isDark ? 0.32 : 0.22} />
                <stop offset='100%' stopColor={stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray='3 4' />
            <XAxis
              dataKey='date'
              tickFormatter={formatDateShort}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
              tickMargin={10}
            />
            <YAxis
              tickFormatter={formatCompact}
              tickLine={false}
              axisLine={false}
              width={72}
              tickMargin={6}
            />
            <Tooltip
              cursor={{ stroke: palette.borderStrong, strokeWidth: 1 }}
              content={
                <ChartTooltip
                  labelFormatter={(label, payload) => {
                    const prevDate = payload?.[0]?.payload?.prevDate;
                    return showPrevious && prevDate
                      ? `${formatDate(label)} · ${formatDate(prevDate)}`
                      : formatDate(label);
                  }}
                  valueFormatter={valueFormatter}
                />
              }
            />
            {showPrevious && (
              <Line
                type='monotone'
                dataKey='prev'
                name={previousLabel}
                stroke={palette.chartPrev}
                strokeWidth={1.5}
                strokeDasharray='4 4'
                dot={false}
                activeDot={{ r: 3 }}
                animationDuration={600}
              />
            )}
            <Area
              type='monotone'
              dataKey='value'
              name={currentLabel}
              stroke={stroke}
              strokeWidth={2}
              fill={`url(#trend-${id})`}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: palette.surface }}
              animationDuration={700}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  },
);
